"use client";

import { useDebounce } from "@uidotdev/usehooks";
import { MapPin, Search } from "lucide-react";
import { startTransition, useEffect, useRef, useState } from "react";
import { searchLocationPlaces } from "@/actions/location-search";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { getLocalityPrimaryLabel } from "@/lib/location-display";
import type {
  LocationBiasCenter,
  LocationSearchPassiveHints,
  LocationSearchProfile,
} from "@/lib/location-search";
import { getLocationSearchBrowserHints } from "@/lib/location-search";
import { createClientLogger } from "@/lib/logging/client";
import type { NominatimPlace } from "@/lib/nominatim";
import { cn } from "@/lib/utils";

const logger = createClientLogger(
  "components/location-search-box/location-search-box.client",
);

interface LocationSearchBoxProps {
  onSelect: (place: NominatimPlace) => void;
  placeholder?: string;
  className?: string;
  /** Merged onto the input itself, so callers can match a surrounding form. */
  inputClassName?: string;
  searchProfile?: LocationSearchProfile;
  biasCenter?: LocationBiasCenter;
  lang?: string;
}

export function LocationSearchBox({
  onSelect,
  placeholder = "Search for a location...",
  className = "",
  inputClassName,
  searchProfile = "locality",
  biasCenter,
  lang,
}: LocationSearchBoxProps) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<NominatimPlace[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const lastSelectedQuery = useRef<string>("");
  const requestIdRef = useRef(0);
  const activeSearchAbortRef = useRef<AbortController | null>(null);
  const [passiveHints] = useState<LocationSearchPassiveHints>(() =>
    getLocationSearchBrowserHints(),
  );
  const liveQueryRef = useRef(query);
  const biasLatitude = biasCenter?.latitude ?? null;
  const biasLongitude = biasCenter?.longitude ?? null;
  liveQueryRef.current = query;

  // Debounce the search query to avoid too many API calls
  const debouncedQuery = useDebounce(query, 300);

  // Perform search when debounced query changes
  useEffect(() => {
    const trimmedQuery = debouncedQuery.trim();
    const liveQuery = liveQueryRef.current.trim();

    if (
      liveQuery === lastSelectedQuery.current ||
      trimmedQuery === lastSelectedQuery.current
    ) {
      setIsLoading(false);
      setIsOpen(false);
      return;
    }

    if (trimmedQuery.length === 0) {
      setResults([]);
      setIsLoading(false);
      setIsOpen(false);
      return;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    activeSearchAbortRef.current?.abort();
    const controller = new AbortController();
    activeSearchAbortRef.current = controller;

    const performSearch = async () => {
      setIsLoading(true);
      try {
        const places = await searchLocationPlaces({
          query: trimmedQuery,
          searchProfile,
          biasCenter:
            biasLatitude !== null && biasLongitude !== null
              ? {
                  latitude: biasLatitude,
                  longitude: biasLongitude,
                }
              : undefined,
          lang,
          passiveHints,
        });

        if (controller.signal.aborted || requestId !== requestIdRef.current) {
          return;
        }

        startTransition(() => {
          setResults(places);
          setIsOpen(true);
        });
      } catch (error) {
        if (controller.signal.aborted) {
          return;
        }

        logger.error("Search error:", error);
        setResults([]);
        setIsOpen(true);
      } finally {
        if (!controller.signal.aborted && requestId === requestIdRef.current) {
          setIsLoading(false);
        }

        if (activeSearchAbortRef.current === controller) {
          activeSearchAbortRef.current = null;
        }
      }
    };

    void performSearch();

    return () => {
      controller.abort();

      if (activeSearchAbortRef.current === controller) {
        activeSearchAbortRef.current = null;
      }
    };
  }, [
    biasLatitude,
    biasLongitude,
    debouncedQuery,
    lang,
    passiveHints,
    searchProfile,
  ]);

  const handleSelect = (place: NominatimPlace) => {
    requestIdRef.current += 1;
    activeSearchAbortRef.current?.abort();
    activeSearchAbortRef.current = null;
    const selectedQuery =
      searchProfile === "locality"
        ? getLocalityPrimaryLabel(place)
        : place.display_name;
    lastSelectedQuery.current = selectedQuery;
    liveQueryRef.current = selectedQuery;
    setQuery(selectedQuery);
    onSelect(place);
    setResults([]);
    setIsLoading(false);
    setIsOpen(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setQuery(newValue);

    // Clear the last selected query when user starts typing something different
    if (newValue !== lastSelectedQuery.current) {
      lastSelectedQuery.current = "";
    }

    // Close dropdown if input is cleared
    if (newValue.trim().length === 0) {
      setIsOpen(false);
    }
  };

  const handleInputFocus = () => {
    if (
      query.trim().length > 0 &&
      query !== lastSelectedQuery.current &&
      results.length > 0
    ) {
      setIsOpen(true);
    }
  };

  const handleInputBlur = () => {
    setTimeout(() => setIsOpen(false), 150);
  };

  return (
    <div className={`relative ${className}`}>
      <div className="relative">
        <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-text-primary" />
        <Input
          type="text"
          value={query}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          placeholder={placeholder}
          className={cn(
            "border-border pr-4 pl-10 focus:border-accent",
            inputClassName,
          )}
        />
        {isLoading && (
          <div className="absolute top-1/2 right-3 h-4 w-4 -translate-y-1/2 animate-spin rounded-full border-2 border-accent border-t-transparent" />
        )}
      </div>

      {/* Results dropdown */}
      {isOpen && results.length > 0 && (
        <Card className="absolute top-full right-0 left-0 z-[9999] mt-1 border-border bg-surface py-0 shadow-xl">
          <CardContent className="max-h-64 overflow-y-auto p-0">
            {results.map((place) => (
              <button
                key={place.place_id}
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => handleSelect(place)}
                className="flex w-full items-start gap-3 border-b border-border p-3 text-left transition-colors last:border-b-0 hover:bg-surface-alt/80"
              >
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-text-primary">
                    {place.display_name.split(",")[0]}
                  </p>
                  <p className="truncate text-xs text-text-primary">
                    {place.display_name}
                  </p>
                </div>
              </button>
            ))}
          </CardContent>
        </Card>
      )}

      {/* No results message */}
      {isOpen &&
        !isLoading &&
        query.trim().length > 0 &&
        results.length === 0 && (
          <Card className="absolute top-full right-0 left-0 z-[9999] mt-1 border-border bg-surface shadow-xl">
            <CardContent className="p-3">
              <p className="text-center text-sm text-text-primary">
                No locations found for "{query}"
              </p>
            </CardContent>
          </Card>
        )}
    </div>
  );
}
